"use client";

import { Heading, ImageWrapper } from "@farm-junction/design-library";
import {
    CAROUSEL_HEADER_ROW_CLASS,
    CarouselViewAllFooter,
    CarouselViewAllHeader,
} from "@/component/ui/CarouselViewAll";
import ImageGallerySection from "@/component/ui/CarImageCard/Imagegallerysection";
import { useImagePageMedia } from "@/component/pdp/image-page/ImagePageMedia";
import type { ImageGallerySectionData } from "@/app/utilities/interfaces";
import type { GalleryColour, ImagePageGalleryData } from "@/component/pdp/image-page/types";

const COLOUR_PREVIEW_LIMIT = 8;

type ModelImageGallerySectionProps = {
    galleryData: ImagePageGalleryData;
    sectionData: ImageGallerySectionData;
    isMobile?: boolean;
    showColours?: boolean;
    coloursPageUrl?: string;
};

function ColourCard({
    colour,
    onClick,
}: {
    colour: GalleryColour;
    onClick?: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={colour.title}
            title={colour.title}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white text-left cursor-pointer"
        >
            <div className="relative aspect-[16/10] bg-[#F4F4F4]">
                <ImageWrapper
                    src={colour.image}
                    alt={colour.alt}
                    title={colour.title}
                    width={320}
                    height={180}
                    className="h-full w-full object-cover"
                />
            </div>
            <div className="flex items-center gap-2 px-3 py-2.5">
                <span
                    className="h-3.5 w-3.5 shrink-0 rounded-full border border-gray-200"
                    style={{ backgroundColor: colour.hex }}
                />
                <span className="truncate text-sm font-medium text-gray-800">{colour.name}</span>
            </div>
        </button>
    );
}

export default function ModelImageGallerySection({
    galleryData,
    sectionData,
    isMobile,
    showColours = false,
    coloursPageUrl = "",
}: ModelImageGallerySectionProps) {
    const { openLightbox } = useImagePageMedia();

    const openAtIndex = (index: number) => {
        const image = galleryData.images[index] ?? galleryData.images[0];
        openLightbox(image?.id, "all", "all");
    };

    const colours = galleryData.colours;
    const previewColours = colours.slice(0, COLOUR_PREVIEW_LIMIT);
    const showColoursGrid = showColours && colours.length > 0;
    const showColoursViewAll = showColoursGrid && Boolean(coloursPageUrl);
    const coloursViewAllTitle = `View All ${galleryData.carName} Colors`;

    return (
        <>
            <ImageGallerySection
                data={sectionData}
                isMobile={isMobile}
                onImageSelect={openAtIndex}
            />

            {showColoursGrid && (
                <section className="mt-8 md:mt-10" aria-labelledby="model-page-colours-heading">
                    <div className={showColoursViewAll ? CAROUSEL_HEADER_ROW_CLASS : "mb-4 md:mb-6"}>
                        <div className="min-w-0 flex-1">
                            <Heading
                                id="model-page-colours-heading"
                                variant="h3"
                                level={3}
                                className="!mb-1 text-lg font-bold text-gray-900 md:text-2xl"
                            >
                                {galleryData.carName} Colors
                            </Heading>
                        </div>
                        {showColoursViewAll && (
                            <CarouselViewAllHeader
                                href={coloursPageUrl}
                                title={coloursViewAllTitle}
                                label="View All Colors"
                            />
                        )}
                    </div>

                    <div className="grid min-h-[160px] grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
                        {previewColours.map((colour) => (
                            <ColourCard
                                key={colour.id}
                                colour={colour}
                                onClick={() => openLightbox(colour.id, "colour", "all")}
                            />
                        ))}
                    </div>

                    {showColoursViewAll && (
                        <CarouselViewAllFooter
                            href={coloursPageUrl}
                            title={coloursViewAllTitle}
                            label="View All Colors"
                            wrapperClassName="mt-4 md:mt-0"
                        />
                    )}
                </section>
            )}
        </>
    );
}
